import { Route, Routes } from 'react-router-dom';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { LinearProgress } from '@mui/material';

import './toaster.css';
import Protected from './components/protected';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ReactGA from 'react-ga4';
import { injectContentsquareScript } from '@contentsquare/tag-sdk';
import { gaTrackingId } from './constants/appConstants';
import { hotJarSiteId } from './constants/appConstants';
import { useToast } from './toast/toastContext';
import { useThemeMode } from './contexts/themeContext';
import socket, { COMPANY_SETTINGS_UPDATED_EVENT } from './socket';
import { user_information, load_field_config } from './redux/actions/action';
import {
  fetchProfileWithRetry,
  PROFILE_RETRY_DELAYS,
  PROFILE_SUPERSEDED
} from './utils/profileFetch';
import { isCompleteProfile } from './redux/reducers/userInformation';
import { setErpTypeInSession } from './services/common';
import { useBanner } from './banner/bannerContext';
import { clearSessionPreservingTab } from './utils/tabSession';
import AskAssistant from './components/askAssistant';
import LocalFolderCheck from './components/localFolderCheck';
import { SOCKET_REFETCH_EVENT } from './hooks/useSocketRefetch';
import { isEmbedded } from './embed/embed';
import EmbedSessionEnded from './embed/embedSessionEnded';

// Route-level code splitting: each page is loaded as its own chunk on demand,
// so opening one route no longer downloads the entire app bundle.
// lazyWithPreload also lets us warm the chunk cache in the background (see the
// idle-prefetch effect below), so navigating between routes is instant instead
// of flashing a blank page while the chunk downloads.
const lazyRoutes = [];
const lazyWithPreload = (factory) => {
  const Component = lazy(factory);
  Component.preload = factory;
  lazyRoutes.push(Component);
  return Component;
};

const Auth = lazyWithPreload(() => import('./pages/auth'));
const AdminHomePage = lazyWithPreload(() => import('./pages/adminHomePage'));
const CanvasLayer1 = lazyWithPreload(() => import('./pages/canvas'));
const Registeration = lazyWithPreload(() => import('./pages/registeration'));
const UserProfile = lazyWithPreload(() => import('./pages/userProfile'));
const CheckoutFormPage = lazyWithPreload(() => import('./pages/checkoutForm'));
const HomePage = lazyWithPreload(() => import('./pages/homePage'));
const VerifiedDetails = lazyWithPreload(() => import('./pages/verfiedDetails'));
const Users = lazyWithPreload(() => import('./pages/users'));
const ThankyouPage = lazyWithPreload(() => import('./pages/thankyou'));
const VendorHomePage = lazyWithPreload(() => import('./vendorPannel/pages/vendorHomePage'));
const GeneratePO = lazyWithPreload(() => import('./pages/generatePO'));
const UnauthorizedPage = lazyWithPreload(() => import('./pages/unauthorisedPage'));
const AdvanceSearch = lazyWithPreload(() => import('./pages/advanceSearch'));
const FAQ = lazyWithPreload(() => import('./pages/faq'));
const Reconsile = lazyWithPreload(() => import('./pages/reconsileStatement'));
const GeneratePoForm = lazyWithPreload(() => import('./pages/generatePoForm'));
const JobBudgetPage = lazyWithPreload(() => import('./pages/jobBudgetPage'));
const FieldsConfigurationPage = lazyWithPreload(() => import('./pages/fieldsConfiguration'));

function RouteFallback() {
  return (
    <LinearProgress
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: (theme) => theme.zIndex.tooltip + 1
      }}
    />
  );
}
//july week 01
ReactGA.initialize(gaTrackingId);

let socketRefetchTimer = null;
// The ids of the documents the notifications in the CURRENT debounce window named.
// They have to ACCUMULATE: the window collapses a burst into one event, so
// carrying just the first or the last payload would drop the one notification
// that named the document a canvas is showing.
let socketRefetchDocIds = [];
// An event that named nothing at all. Listeners must read `documentIds: null` as
// "refetch anyway" rather than as "not for you" — we would rather refresh
// needlessly than leave a screen stale on a payload shape we did not anticipate.
let socketRefetchUnscoped = false;
const dispatchSocketRefetchDebounced = (arg) => {
  const ids = Array.isArray(arg?.documentId) ? arg.documentId.filter(Boolean) : [];
  if (ids.length) socketRefetchDocIds.push(...ids.map(String));
  else socketRefetchUnscoped = true;

  if (socketRefetchTimer) return;
  socketRefetchTimer = setTimeout(() => {
    const detail = {
      documentIds: socketRefetchUnscoped ? null : [...new Set(socketRefetchDocIds)]
    };
    socketRefetchDocIds = [];
    socketRefetchUnscoped = false;
    socketRefetchTimer = null;
    window.dispatchEvent(new CustomEvent(SOCKET_REFETCH_EVENT, { detail }));
  }, 2000);
};

let userInfoTimer = null;

// Deliberately does not promise that signing out and back in fixes it — for the
// customer hitting this, it repeatedly didn't. Says what is untrustworthy on
// screen instead, so nothing wrong reaches the ERP while the profile is missing.
const PROFILE_LOAD_FAILED =
  "We couldn't load your account settings. Some features are hidden and the coding shown may be incomplete — please don't approve or export until this clears. Refresh to retry, and contact support if it persists.";

const getUserRole = () => {
  return sessionStorage.getItem('userRole') || 'guest';
};

injectContentsquareScript({
  siteId: hotJarSiteId,
  async: true,
  defer: false
});

function App() {
  const { showToast } = useToast();
  const { showBanner, hideBanner } = useBanner();
  const { nonce: themeNonce } = useThemeMode();
  const navigate = useNavigate();

  history.navigate = useNavigate();
  history.location = useLocation();
  const dispatch = useDispatch();

  const userRole = getUserRole();

  const userDetails = useSelector((state) => state?.userInfo?.userInfo);

  const userId = userDetails?.user?._id;
  const token = sessionStorage.getItem('token');

  // Company-level switch (Settings → Coding Rules → Real-Time Notifications).
  // When it's off we never surface a socket notification — the event only
  // triggers the background API refetch.
  const notificationPermission = userDetails?.company?.notificationPermission;

  const cognitoUsername =
    useSelector((state) => state?.signIn?.signIn?.cognitoRes?.accessToken?.payload?.username) ||
    sessionStorage.getItem('cognitoUsername');

  // Guards the recovery effect below against overlapping runs: the retries are
  // async, so without this a re-render mid-backoff starts a second chain.
  const profileFetchRef = useRef(false);
  // Whether the profile banner is ours to retract, so we never dismiss a banner
  // some other feature raised.
  const profileBannerRef = useRef(false);
  // Latest profile, readable from inside an async chain that closed over an
  // older render's `userDetails`.
  const userDetailsRef = useRef(userDetails);
  userDetailsRef.current = userDetails;
  // Re-arms the recovery effect after a chain abandoned itself to a new session.
  const [profileAttempt, setProfileAttempt] = useState(0);

  const userInfo = ({ retries = 0 } = {}) =>
    fetchProfileWithRetry({
      cognitoUsername,
      retries,
      onProfile: (profile) => {
        dispatch(user_information(profile));
        // Recovery has to restore the session ERP type too. The degraded payload
        // this path exists to survive is exactly the one that made signIn.jsx
        // remove the key, and validationService reads it directly in five
        // places — without this the session would validate against the generic
        // rules with a perfectly good Vista profile in the store.
        if (profile?.user?.erpType) {
          setErpTypeInSession(profile.user.erpType);
        }
        // Same reasoning for the field configuration: it replaced eight company
        // permissions, so without it the session codes against the wrong columns
        // and syncs the wrong ERP lists.
        if (profile?.company?._id) {
          dispatch(load_field_config(profile.company._id));
        }
      },
      onUnauthorized: () => {
        clearSessionPreservingTab();
        navigate('/');
      }
    });

  // Warm the route chunk cache after the current page has painted, during idle
  // time. Once a chunk is cached, navigating to that route resolves instantly,
  // so the user never sees a blank page or the loading bar between routes.
  useEffect(() => {
    const requestIdle = window.requestIdleCallback || ((cb) => setTimeout(() => cb(), 300));
    const cancelIdle = window.cancelIdleCallback || clearTimeout;

    const handle = requestIdle(() => {
      lazyRoutes.forEach((route) => {
        if (route.preload) route.preload();
      });
    });

    return () => cancelIdle(handle);
  }, []);

  // Re-fetch user info on refresh when persist data is lost
  // (token survives in sessionStorage but Redux state is empty).
  //
  // If the retries are exhausted and we still have no profile, say so. Without
  // this the app renders its "denied" state when it means "unknown" — every
  // permission-gated column and button silently absent — and the user is left
  // to work out from missing features that anything is wrong at all.
  //
  // Completeness — stored AND able to authorise — rather than the reducer's
  // storage rule. Testing `userDetails.user` alone would let a stored profile
  // whose `user` has no `_id` block recovery outright; testing `isUsableProfile`
  // alone let a profile with `role: ''` do the same, which is how Frank M. Booth
  // sat in a fully locked-down UI on 2026-08-13 with no fetch and no banner.
  //
  // Hoisted so both effects below key on the predicate itself rather than on
  // `user._id`: the id is identical either side of that failure, so an id-keyed
  // effect cannot tell the two states apart.
  const profileComplete = isCompleteProfile(userDetails);

  useEffect(() => {
    if (!token || !cognitoUsername || profileComplete) return;
    if (profileFetchRef.current) return;
    profileFetchRef.current = true;
    userInfo({ retries: PROFILE_RETRY_DELAYS.length })
      .then((loaded) => {
        // Only a genuine exhausted-retries failure warrants the banner. A 401
        // clears the session and redirects, and a superseded chain belongs to a
        // session that no longer exists — neither is this user's problem.
        //
        // Re-read the store at resolution time rather than through this
        // closure: one of the other screens may have recovered the profile
        // during the backoff, and warning over a healthy session would strand
        // the banner — `user._id` never changes again, so the retraction effect
        // below would never fire.
        if (
          loaded === false &&
          sessionStorage.getItem('token') &&
          !isCompleteProfile(userDetailsRef.current)
        ) {
          profileBannerRef.current = true;
          showBanner(PROFILE_LOAD_FAILED, 'error');
        }
        // A superseded chain means a new session started while we were in
        // backoff. None of this effect's other dependencies change on the way
        // through, so without an explicit nudge the new session would inherit
        // the old chain's abandonment and get no recovery at all. Bumped only
        // on supersede, so a plain failure can never loop.
        if (loaded === PROFILE_SUPERSEDED) {
          setProfileAttempt((n) => n + 1);
        }
      })
      // A throw anywhere in the chain must not swallow the warning: that would
      // leave the session half-permissioned and silent, which is the whole
      // failure mode this exists to end.
      .catch(() => {
        if (sessionStorage.getItem('token') && !isCompleteProfile(userDetailsRef.current)) {
          profileBannerRef.current = true;
          showBanner(PROFILE_LOAD_FAILED, 'error');
        }
      })
      // finally, not then: a throw would otherwise latch the guard on and
      // disable recovery for the rest of the session.
      .finally(() => {
        profileFetchRef.current = false;
      });
  }, [token, cognitoUsername, profileComplete, profileAttempt]);

  // Retract the warning however the profile came back. ~15 other screens fetch
  // and dispatch it themselves, so tying the retraction to this file's own
  // fetch would strand a "don't approve or export" banner over a healthy
  // session. Watching the store covers every one of them.
  //
  // Also retract when the session ends without a profile: an SPA 401 redirect
  // leaves `user._id` undefined forever, so the banner would otherwise sit on
  // the login page until a hard reload.
  // Keyed on the predicate itself rather than on `user._id`, so it still fires
  // if usability ever flips without the id changing.
  useEffect(() => {
    if (profileBannerRef.current && (profileComplete || !token)) {
      profileBannerRef.current = false;
      hideBanner();
    }
  }, [profileComplete, token]);

  //cache issue fix - reload app if new version is deployed

  useEffect(() => {
    const CHECK_INTERVAL = 60000; // Check every 60 seconds

    const checkVersion = async () => {
      try {
        const response = await fetch('/meta.json?t=' + Date.now(), {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        const data = await response?.json();
        const currentVersion = sessionStorage.getItem('appVersion');

        if (!currentVersion) {
          sessionStorage.setItem('appVersion', data?.version);
          return;
        }

        if (data?.version !== currentVersion) {
          sessionStorage.setItem('appVersion', data?.version);
          // Clear all caches before reloading
          if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames?.map((name) => caches.delete(name)));
          }
          window.location.reload(true);
        }
      } catch (error) {
        console.error('Version check failed:', error);
      }
    };

    checkVersion();
    const intervalId = setInterval(checkVersion, CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!token || !userId) return;
    socket.connect(); // Connect only when userId is available
    socket.emit('register', userId);

    socket.on('notification', (arg) => {
      if (arg?.metadata?.action !== 'api_call') {
        if (!userInfoTimer) {
          userInfoTimer = setTimeout(() => {
            // Background refresh: a failure here is not worth interrupting the
            // user over — the recovery effect owns that — but it must not
            // surface as an unhandled rejection either.
            userInfo().catch(() => {});
            userInfoTimer = null;
          }, 5000);
        }
      }
      if (arg?.metadata?.action === 'api_call') {
        dispatchSocketRefetchDebounced(arg);
      }
      if (arg?.metadata?.action === 'notification_and_call') {
        dispatchSocketRefetchDebounced(arg);
        if (notificationPermission) showToast(arg?.metadata?.message, 'success');
      }
      if (arg?.metadata?.action === 'notification' && notificationPermission) {
        showToast(arg?.metadata?.message, 'success');
      }
    });

    // Company settings changed in another session (an owner flipping a switch
    // while a support user has the screen open). Every session holds a copy of
    // those settings in its persisted store, and a page refresh does not
    // refetch a complete profile — so without this the copy stayed stale until
    // logout. Debounced through the same timer as the notification refresh.
    socket.on(COMPANY_SETTINGS_UPDATED_EVENT, () => {
      if (!userInfoTimer) {
        userInfoTimer = setTimeout(() => {
          userInfo().catch(() => {});
          userInfoTimer = null;
        }, 1000);
      }
    });

    return () => {
      if (socket) {
        socket.off('notification'); // prevent duplicate listeners
        socket.off(COMPANY_SETTINGS_UPDATED_EVENT);
        socket.disconnect();
      }
    };
  }, [token, userId]); // Ensure it runs again only if userId changes

  return (
    <>
      <Suspense fallback={<RouteFallback />}>
        <Routes key={themeNonce}>
          <Route
            path="/admin"
            element={
              <Protected role={userRole}>
                <AdminHomePage />
              </Protected>
            }
          />
          <Route
            path="/vendor"
            element={
              <Protected role={userRole}>
                <VendorHomePage />
              </Protected>
            }
          />

          <Route
            path="/canvas/:id"
            element={
              <Protected role={userRole}>
                <CanvasLayer1 />
              </Protected>
            }
          />
          <Route
            path="/users"
            element={
              <Protected role={userRole}>
                <Users />
              </Protected>
            }
          />
          <Route
            path="/userProfile"
            element={
              <Protected role={userRole}>
                <UserProfile />
              </Protected>
            }
          />
          <Route
            path="/document-configuration"
            element={
              <Protected role={userRole}>
                <FieldsConfigurationPage />
              </Protected>
            }
          />
          <Route
            path="/details/:id"
            element={
              <Protected role={userRole}>
                <VerifiedDetails />
              </Protected>
            }
          />

          <Route
            path="/generatePo/:id"
            element={
              <Protected role={userRole}>
                <GeneratePO />
              </Protected>
            }
          />

          <Route
            path="/home"
            element={
              <Protected role={userRole}>
                <HomePage />
              </Protected>
            }
          />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/paymentSuccessful" element={<ThankyouPage />} />
          <Route path="/stripe" element={<CheckoutFormPage />} />
          <Route path="/" element={isEmbedded ? <EmbedSessionEnded /> : <Auth />} />
          <Route path="*" element={<Navigate to="/unauthorized" />} />
          <Route path="/:role" element={<Registeration />} />
          <Route
            path="/search"
            element={
              <Protected role={userRole}>
                <AdvanceSearch />
              </Protected>
            }
          />
          <Route
            path="/FAQ"
            element={
              <Protected role={userRole}>
                <FAQ />
              </Protected>
            }
          />
          <Route
            path="/statements-reconciliation"
            element={
              <Protected role={userRole}>
                <Reconsile />
              </Protected>
            }
          />
          <Route
            path="/generatePo"
            element={
              <Protected role={userRole}>
                <GeneratePoForm />
              </Protected>
            }
          />
          <Route
            path="/job-budgets"
            element={
              <Protected role={userRole}>
                <JobBudgetPage />
              </Protected>
            }
          />
        </Routes>
      </Suspense>
      {/* Outside <Routes> on purpose. The assistant holds a live iframe that is
          warmed in the background and must survive navigation, so it cannot sit
          anywhere the router (or the themeNonce key above) can unmount it.
          faqMaintenance is the admin kill switch that already hides the FAQ. */}
      {!userDetails?.faqMaintenance && <AskAssistant />}
      {/* The local folder check: in the background, on the computer that runs
          Foundation, for a company whose invoice images go to Google Drive. */}
      {token && userDetails?.company?.isGoogleConnected && userDetails?.company?._id && (
        <LocalFolderCheck
          companyId={userDetails.company._id}
          savedPath={userDetails?.company?.folderPath || userDetails?.user?.folderPath || ''}
        />
      )}
    </>
  );
}

export default App;
